import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { FileText, CalendarPlus, Play, ClipboardList } from 'lucide-react';
import { useAlert } from '../../context/AlertContext';
import { usePermissions } from '../../context/PermissionContext';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { useSite } from '../../context/SiteContext';

/**
 * The audit lifecycle, in the order it is performed.
 *
 * Template -> Schedule -> Conduct. Each step depends on the one before it: a
 * schedule needs a template to apply, and an audit cannot be started before it
 * has been scheduled. Audit Records carries no step number because it is the
 * register, not a step.
 *
 * `requiresCreate` gates the two cards that write.
 */
const AUDIT_FLOW = [
  {
    key: 'templates', step: 1,
    title: 'Audit Templates', subtitle: 'Define the checklist',
    route: '/audits/templates',
    cls: 'bg-indigo-600 hover:bg-indigo-700', Icon: FileText,
  },
  {
    key: 'schedule', step: 2, requiresCreate: true,
    title: 'Schedule Audit', subtitle: 'Plan it against a template',
    route: '/audits/schedule',
    cls: 'bg-purple-600 hover:bg-purple-700', Icon: CalendarPlus,
  },
  {
    key: 'start', step: 3, requiresCreate: true,
    title: 'Start Audit', subtitle: 'Conduct a scheduled audit',
    route: '/audits/records',
    cls: 'bg-green-600 hover:bg-green-700', Icon: Play,
  },
  {
    key: 'records',
    title: 'Audit Records', subtitle: 'View all audits and findings',
    route: '/audits/records',
    cls: 'bg-teal-600 hover:bg-teal-700', Icon: ClipboardList,
  },
];

const AuditsDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [stats, setStats] = useState({
    upcoming: 0,
    overdue: 0,
    completed: 0,
    capaPending: 0
  });
  const [upcomingAudits, setUpcomingAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);

  // REL-18: audits are site-scoped; reload when the active site changes.
  const { activeSiteId } = useSite();

  useEffect(() => {
    fetchData();
  }, [activeSiteId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const params = activeSiteId ? { site_id: activeSiteId } : {};
      const [schedulesRes, recordsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/audit-schedules`, { headers, params }),
        axios.get(`${API}/audit-records`, { headers, params }),
        axios.get(`${API}/audit-templates`, { headers })
      ]);

      const schedules = schedulesRes.data;
      const records = recordsRes.data;

      // Calculate stats
      const now = new Date();
      const upcoming = schedules.filter(s => {
        const schedDate = new Date(s.scheduled_date);
        return schedDate > now && s.status === 'Scheduled';
      }).length;

      const overdue = schedules.filter(s => {
        const schedDate = new Date(s.scheduled_date);
        return schedDate < now && s.status === 'Scheduled';
      }).length;

      const completed = records.filter(r => r.workflow_status === 'Closed').length;
      const capaPending = records.filter(r => r.workflow_status === 'CAPA Outstanding').length;

      setStats({ upcoming, overdue, completed, capaPending });
      setUpcomingAudits(schedules.filter(s => s.status === 'Scheduled'));

      // Filter out templates that already have active (non-Closed) audit records
      const activeTemplateIds = new Set(
        records
          .filter(r => r.workflow_status !== 'Closed')
          .map(r => r.template_id)
      );
      const availableTemplates = (templatesRes.data || []).filter(
        t => !activeTemplateIds.has(t.id)
      );
      setTemplates(availableTemplates);
    } catch (err) {
      console.error('Failed to fetch audit data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartAuditFromSchedule = async (schedule) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Look up template to get audit_type (not stored on schedule)
      const template = templates.find(t => t.id === schedule.template_id);

      // Create audit record from schedule
      const auditData = {
        audit_title: schedule.audit_title,
        audit_type: template?.audit_type || 'Internal',
        template_id: schedule.template_id,
        template_name: schedule.template_name || template?.template_name || '',
        area: schedule.area || '',
        location: schedule.location || '',
        department: schedule.department || '',
        lead_auditor_id: schedule.auditor_id || user.id,
        lead_auditor_name: schedule.auditor_name || user.name,
        // Carry the team planned on the schedule onto the audit record
        co_auditors: (schedule.co_auditors || []).filter(
          c => c.user_id !== (schedule.auditor_id || user.id)
        ),
        audit_date: schedule.scheduled_date,
        schedule_id: schedule.id,
        // REL-18: the audit belongs to the schedule's site
        site_id: schedule.site_id || activeSiteId || undefined,
        findings: []
      };

      const response = await axios.post(`${API}/audit-records`, auditData, { headers });
      
      // Update schedule status
      await axios.put(
        `${API}/audit-schedules/${schedule.id}`,
        { status: 'In Progress' },
        { headers }
      );

      // Remove from local state immediately so card disappears
      setUpcomingAudits(prev => prev.filter(a => a.id !== schedule.id));

      // Navigate to conduct audit with schedule in state
      navigate('/audits/conduct', { state: { schedule, auditRecordId: response.data.id } });
    } catch (err) {
      showError('Failed to start audit: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">🔍 Audits & Inspections</h1>
          <p className="text-gray-600 mt-1">Manage compliance audits and safety inspections</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-blue-50 rounded-xl shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-semibold">Upcoming Audits</p>
                <p className="text-3xl font-bold text-blue-700 mt-2">{stats.upcoming}</p>
              </div>
              <div className="text-blue-500">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-red-50 rounded-xl shadow-md p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-semibold">Overdue</p>
                <p className="text-3xl font-bold text-red-700 mt-2">{stats.overdue}</p>
              </div>
              <div className="text-red-500">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-green-50 rounded-xl shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-semibold">Completed</p>
                <p className="text-3xl font-bold text-green-700 mt-2">{stats.completed}</p>
              </div>
              <div className="text-green-500">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 rounded-xl shadow-md p-6 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600 font-semibold">CAPA Pending</p>
                <p className="text-3xl font-bold text-yellow-700 mt-2">{stats.capaPending}</p>
              </div>
              <div className="text-yellow-500">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Action cards, in WORKFLOW ORDER.
            An audit cannot be scheduled without a template to schedule, and
            cannot be started without having been scheduled — so the cards read
            1, 2, 3 in the order the work actually happens. They used to open
            with "Start Audit", which is the last step, and finish with
            Templates, which is the first.

            Steps 1-3 are numbered because this genuinely IS a sequence. Audit
            Records is deliberately NOT numbered: it is the register you consult,
            not a fourth step you perform. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {AUDIT_FLOW
            .filter((card) => !card.requiresCreate || hasPermission('audits', 'create'))
            .map(({ key, step, title, subtitle, route, cls, Icon }) => (
              <button
                key={key}
                onClick={() => navigate(route)}
                className={`${cls} text-white p-6 rounded-xl shadow-md transition-colors text-left`}
              >
                <div className="flex items-center gap-4">
                  <Icon className="w-10 h-10 flex-shrink-0" />
                  <div className="min-w-0">
                    {step && (
                      <p className="text-[11px] font-semibold uppercase tracking-wide opacity-75">
                        Step {step}
                      </p>
                    )}
                    <h3 className="text-lg font-bold">{title}</h3>
                    <p className="text-sm opacity-90">{subtitle}</p>
                  </div>
                </div>
              </button>
            ))}
        </div>

        {/* Upcoming Audits List */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Scheduled Audits</h2>
          {upcomingAudits.length > 0 ? (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {upcomingAudits.map(audit => (
                <div key={audit.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800">{audit.audit_title}</h3>
                    <p className="text-sm text-gray-600">Auditor: {audit.auditor_name}</p>
                    {audit.location && <p className="text-xs text-gray-500">Location: {audit.location}</p>}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-700">
                        {formatDate(audit.scheduled_date)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatTime(audit.scheduled_date)}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate('/audits/schedule', { state: { editSchedule: audit } })}
                      className="flex items-center space-x-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span className="font-semibold text-sm">Edit</span>
                    </button>
                    <button
                      onClick={() => handleStartAuditFromSchedule(audit)}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      <span className="font-semibold">Start Audit</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-center py-8">No scheduled audits</p>
          )}
        </div>

      </div>
    </div>
      </div>
  );
};

export default AuditsDashboard;
